import { useMemo } from "react";

import { type Chart } from "chart.js";
import clsx from "clsx";
import { groupBy } from "lodash";
import moment from "moment";
import { Bar } from "react-chartjs-2";

import { Spinner } from "@components/v2/loading-indicator/spinner";
import { FETCH_PROCESS_ORDERS_V4 } from "@gql/process-orders";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchProcessOrdersV4,
  type FetchProcessOrdersV4Variables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { BackgroundPlugin } from "../../../features/orders-v2/chart-plugins/background-plugin";

type Props = {
  className?: string;
  previousProcessedAt: string;
};

function getCssVariable(name: string, from: HTMLElement = document.body) {
  return getComputedStyle(from).getPropertyValue(name).trim();
}

type BarChart = Chart<"bar", { x: string; y: number | null }[], string>;

export const ProcessingChart = ({ className, previousProcessedAt }: Props) => {
  const { data, loading } = useNorthbeamQuery<
    FetchProcessOrdersV4,
    FetchProcessOrdersV4Variables
  >(FETCH_PROCESS_ORDERS_V4, {
    variables: { startDate: previousProcessedAt },
  });

  const chartData = useMemo<BarChart["data"]>(() => {
    const processedOrders = data?.me.processedOrdersV4.processedOrders ?? [];
    const processingOrders = data?.me.processingOrdersV4.processingOrders ?? [];
    const notProcessedOrders =
      data?.me.notProcessedOrdersV4.notProcessedOrders ?? [];

    const labels = extendTimeRange(
      previousProcessedAt,
      moment().startOf("hour").toISOString(),
    );

    const datasets = [
      buildHourlyDataset(processedOrders, labels, "#50BA58", "Attributed"),
      buildHourlyDataset(
        processingOrders,
        labels,
        "#ADDFB1",
        "Calculating Attribution",
      ),
      buildHourlyDataset(notProcessedOrders, labels, "#E6EDEA", "In Queue"),
    ].filter((d) => d.data.length > 0);

    return { labels, datasets };
  }, [data, previousProcessedAt]);

  const options: BarChart["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        stacked: false,
        ticks: {
          color: "#6B7280",
          stepSize: 5000,
          callback: function (value: string | number) {
            return `${Math.round(Number(value))}`;
          },
          count: 4,
        },
        grid: {
          drawBorder: false,
          drawOnChartArea: true,
          color: (context) => {
            if (
              context.index === 0 ||
              context.index === context.chart.scales.y.ticks.length - 1
            ) {
              return "";
            }
            return getCssVariable("--v2-border-tertiary");
          },
          drawTicks: false,
        },
        title: {
          display: true,
          text: "Orders",
          color: "#021330",
          font: {
            family: "TT Commons Pro, sans-serif",
            size: 12,
            weight: "600",
            lineHeight: "16px",
          },
          padding: { top: 0, bottom: 10 },
        },
      },
      x: {
        type: "time",
        time: {
          unit: "hour",
          round: "hour",
          tooltipFormat: "MMM D, hA",
          displayFormats: {
            hour: "ha",
          },
          stepSize: 1,
        },
        stacked: true,
        ticks: {
          color: "#6B7280",
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          source: "data",
          callback: (val, index) => {
            const label = chartData.labels?.[index];
            if (label && index % 2 === 0) {
              return moment(label).format("ha");
            }
            return "";
          },
        },
        grid: {
          drawBorder: false,
          drawOnChartArea: true,
          color: (context) => {
            if (
              context.index === 0 ||
              context.index === context.chart.scales.x.ticks.length - 1
            ) {
              return "";
            }
            return getCssVariable("--v2-border-tertiary");
          },
          borderDash: [2, 2],
          tickLength: 0,
          offset: false,
          drawTicks: false,
        },
        offset: false,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          color: "#374151",
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0]?.label;
            return label ? label : "";
          },
          label: (tooltipItem) => {
            const value = (tooltipItem.raw as { y: number })?.y;
            if (!value) {
              return "";
            }
            return `${tooltipItem.dataset.label}: ${value}`;
          },
        },
        filter: (tooltipItem) => {
          const value = (tooltipItem.raw as { y: number })?.y;
          return value > 0;
        },
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
  };

  return (
    <div
      className={clsx(
        "h-64 w-full relative border-v2-tertiary border rounded-md p-4 flex flex-col gap-2",
        className,
      )}
    >
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex flex-row gap-2 items-center justify-between">
            <div className="text-v2-primary font-tt-commons-pro text-base font-semibold leading-6">
              Realtime Orders in past 48 hours
            </div>
            <div className="flex flex-row gap-2 items-center justify-between">
              <div className="text-v2-secondary font-tt-commons-pro text-xs font-normal leading-4">
                Attributed{" "}
                <span className="text-v2-primary font-tt-commons-pro text-xs font-semibold leading-4">
                  {data?.me.processedOrdersV4.processedOrders.length}
                </span>
              </div>
              <div className="text-v2-secondary font-tt-commons-pro text-xs font-normal leading-4">
                Calculating Attribution{" "}
                <span className="text-v2-primary font-tt-commons-pro text-xs font-semibold leading-4">
                  {data?.me.processingOrdersV4.processingOrders.length}
                </span>
              </div>
              <div className="text-v2-secondary font-tt-commons-pro text-xs font-normal leading-4">
                In Queue{" "}
                <span className="text-v2-primary font-tt-commons-pro text-xs font-semibold leading-4">
                  {data?.me.notProcessedOrdersV4.notProcessedOrders.length}
                </span>
              </div>
            </div>
          </div>
          <div className="flex-grow relative overflow-hidden">
            <Bar
              data={chartData}
              options={options}
              plugins={[BackgroundPlugin]}
            />
          </div>
        </>
      )}
    </div>
  );
};

const buildHourlyDataset = (
  orders: { occurred_at: string }[],
  allHours: string[],
  color: string,
  label: string,
) => {
  const hourlyGroups = groupBy(orders, (order) =>
    moment(order.occurred_at).startOf("hour").toISOString(),
  );

  return {
    label,
    backgroundColor: color,
    data: allHours.map((hour) => {
      const value = hourlyGroups[hour]?.length ?? 0;
      return {
        x: hour,
        y: value === 0 ? null : value,
      };
    }),
    barThickness: 9,
    maxBarThickness: 9,
    borderRadius: 5,
    skipNull: true,
  };
};

const extendTimeRange = (startISO: string, endISO: string): string[] => {
  const start = moment(startISO).startOf("hour");
  const end = moment(endISO).startOf("hour");
  const hours = [];

  let current = start.clone();
  while (current <= end) {
    hours.push(current.toISOString());
    current = current.add(1, "hour");
  }

  return hours;
};
